import type { ResellerClientRegister, ResellerClientRow } from "./api";

/**
 * The pure half of the console's list of every reseller's clients (tenant
 * plan phase 21): the months to choose from, the words for a client's state,
 * and the CSV Jared invoices from. Days are South African days.
 */

const SA_OFFSET_MS = 2 * 3_600_000;

/** This month in South Africa, YYYY-MM. */
export function saMonth(now: Date = new Date()): string {
  return new Date(now.getTime() + SA_OFFSET_MS).toISOString().slice(0, 7);
}

/** This month and the 23 before it, newest first, with their names. */
export function monthChoices(now: Date = new Date()): Array<{ month: string; label: string }> {
  const [y, m] = saMonth(now).split("-").map(Number);
  const out: Array<{ month: string; label: string }> = [];
  for (let back = 0; back < 24; back++) {
    const d = new Date(Date.UTC(y, m - 1 - back, 15));
    out.push({
      month: d.toISOString().slice(0, 7),
      label: d.toLocaleDateString("en-ZA", { month: "long", year: "numeric", timeZone: "UTC" })
    });
  }
  return out;
}

/** A South African day for a spreadsheet, or empty. */
export function csvDay(iso: string | null | undefined): string {
  return iso ? new Date(Date.parse(iso) + SA_OFFSET_MS).toISOString().slice(0, 10) : "";
}

/** "Live", "Paused" or "Let go": a client the reseller removed is still listed for the days it was live. */
export function stateOf(r: Pick<ResellerClientRow, "enabled" | "current">): "Live" | "Paused" | "Let go" {
  if (!r.current) return "Let go";
  return r.enabled ? "Live" : "Paused";
}

export type StatusFilter = "all" | "live" | "paused" | "live_in_month";

export function filterRows(rows: readonly ResellerClientRow[], resellerId: string, status: StatusFilter): ResellerClientRow[] {
  return rows.filter((r) => {
    if (resellerId && r.resellerId !== resellerId) return false;
    if (status === "live") return r.current && r.enabled;
    if (status === "paused") return !(r.current && r.enabled);
    if (status === "live_in_month") return r.daysLive > 0;
    return true;
  });
}

/** One row per client, for invoicing by hand. */
export function registerCsv(register: ResellerClientRegister, rows: readonly ResellerClientRow[]) {
  return {
    filename: `reseller-clients-${register.month}.csv`,
    headers: [
      "Reseller",
      "Client",
      "State",
      "Live since",
      "Not live since",
      "Set up",
      "First sign-in",
      `Days live in ${register.month}`,
      "People",
      "Joined",
      "Active people",
      "Last activity"
    ],
    rows: rows.map((r) => [
      r.resellerName,
      r.name,
      stateOf(r),
      csvDay(r.liveSince),
      csvDay(r.pausedSince),
      csvDay(r.setUpAt),
      csvDay(r.firstSignInAt),
      r.daysLive,
      r.people,
      r.joined,
      r.activePeople,
      csvDay(r.lastActivityAt)
    ])
  };
}
